import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  BarChart3,
  Download,
  Calendar as CalendarIcon,
  MapPin,
  Loader2,
  FileSpreadsheet,
  FileText,
  QrCode
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";
import { format, parseISO } from "date-fns";
import { cn } from "@/lib/utils";

const COLORS = ["#2563eb", "#7c3aed", "#db2777", "#ea580c", "#16a34a", "#0891b2"];

export default function Reports() {
  const { token } = useAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [dailyReport, setDailyReport] = useState(null);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [customReport, setCustomReport] = useState(null);
  const [customStartDate, setCustomStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 30)));
  const [customEndDate, setCustomEndDate] = useState(new Date());
  const [appliedStartDate, setAppliedStartDate] = useState(null);
  const [appliedEndDate, setAppliedEndDate] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState("daily");

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data);
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  const fetchDailyReport = async () => {
    try {
      const response = await axios.get(`${API}/reports/daily`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          date: format(selectedDate, "yyyy-MM-dd"),
          location_id: selectedLocation !== "all" ? selectedLocation : undefined
        }
      });
      setDailyReport(response.data);
    } catch (error) {
      console.error("Error fetching daily report:", error);
      toast.error("Failed to load daily report");
    }
  };

  const fetchMonthlyReport = async () => {
    try {
      const response = await axios.get(`${API}/reports/monthly`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          year: selectedYear,
          month: selectedMonth,
          location_id: selectedLocation !== "all" ? selectedLocation : undefined
        }
      });
      setMonthlyReport(response.data);
    } catch (error) {
      console.error("Error fetching monthly report:", error);
      toast.error("Failed to load monthly report");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomReport = async (startDate, endDate) => {
    if (!startDate || !endDate) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/custom`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start_date: format(startDate, "yyyy-MM-dd"),
          end_date: format(endDate, "yyyy-MM-dd"),
          location_id: selectedLocation !== "all" ? selectedLocation : undefined
        }
      });
      setCustomReport(response.data);
    } catch (error) {
      console.error("Error fetching custom report:", error);
      toast.error("Failed to load custom report");
    } finally {
      setLoading(false);
    }
  };

  const handleApplyCustomRange = () => {
    if (customStartDate > customEndDate) {
      toast.error("Start date must be before end date");
      return;
    }
    setAppliedStartDate(customStartDate);
    setAppliedEndDate(customEndDate);
    fetchCustomReport(customStartDate, customEndDate);
  };

  useEffect(() => {
    fetchLocations();
  }, [token]);

  useEffect(() => {
    fetchDailyReport();
  }, [token, selectedDate, selectedLocation]);

  useEffect(() => {
    fetchMonthlyReport();
  }, [token, selectedYear, selectedMonth, selectedLocation]);

  // Re-fetch custom report when location changes (only if already applied)
  useEffect(() => {
    if (appliedStartDate && appliedEndDate) {
      fetchCustomReport(appliedStartDate, appliedEndDate);
    }
  }, [selectedLocation]);

  const handleExport = async (reportType, exportFormat) => {
    setExporting(true);
    try {
      const params = {
        report_type: reportType,
        format: exportFormat
      };

      if (reportType === "daily") {
        params.date = format(selectedDate, "yyyy-MM-dd");
      } else if (reportType === "custom") {
        if (!appliedStartDate || !appliedEndDate) {
          toast.error("Please apply a date range first");
          setExporting(false);
          return;
        }
        params.start_date = format(appliedStartDate, "yyyy-MM-dd");
        params.end_date = format(appliedEndDate, "yyyy-MM-dd");
      } else {
        params.year = selectedYear;
        params.month = selectedMonth;
      }

      if (selectedLocation !== "all") {
        params.location_id = selectedLocation;
      }

      const response = await axios.get(`${API}/reports/export`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        responseType: "blob"
      });

      // Generate filename based on report type
      let filename;
      if (reportType === "daily") {
        filename = `check_ins_${format(selectedDate, "yyyy-MM-dd")}`;
      } else if (reportType === "custom") {
        filename = `check_ins_${format(appliedStartDate, "yyyy-MM-dd")}_to_${format(appliedEndDate, "yyyy-MM-dd")}`;
      } else {
        filename = `check_ins_${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;
      }

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${filename}.${exportFormat}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Report exported successfully");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export report");
    } finally {
      setExporting(false);
    }
  };

  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" }
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reports</h1>
          <p className="text-gray-500 mt-1">
            View and export check-in reports
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedLocation} onValueChange={setSelectedLocation}>
            <SelectTrigger className="w-[200px]" data-testid="location-filter">
              <MapPin className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Locations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {locations.map((loc) => (
                <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="daily">Daily Report</TabsTrigger>
          <TabsTrigger value="monthly">Monthly Report</TabsTrigger>
          <TabsTrigger value="custom">Custom Range</TabsTrigger>
        </TabsList>

        {/* Daily Report */}
        <TabsContent value="daily" className="space-y-6">
          {/* Date Picker */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="justify-start" data-testid="date-picker-btn">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(selectedDate, "PPP")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleExport("daily", "csv")}
                  disabled={exporting}
                  data-testid="export-daily-csv-btn"
                >
                  {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
                  Export CSV
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleExport("daily", "xlsx")}
                  disabled={exporting}
                  data-testid="export-daily-xlsx-btn"
                >
                  {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />}
                  Export Excel
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Daily Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-sm text-gray-500">Total Check-ins</p>
                  <p className="text-4xl font-bold text-gray-900 mt-2">
                    {dailyReport?.total_check_ins || 0}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-sm text-gray-500">Locations Active</p>
                  <p className="text-4xl font-bold text-gray-900 mt-2">
                    {dailyReport?.by_location?.length || 0}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-sm text-gray-500">Date</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">
                    {format(selectedDate, "MMM d, yyyy")}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* By Location Chart */}
          {dailyReport?.by_location?.length > 0 && (
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle>Check-ins by Location</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={dailyReport.by_location}
                        dataKey="count"
                        nameKey="location_name"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {dailyReport.by_location.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Daily Check-ins Table */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Check-in Details</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Member</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead>Checked In By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dailyReport?.check_ins?.length > 0 ? (
                    dailyReport.check_ins.map((checkIn) => (
                      <TableRow key={checkIn.id}>
                        <TableCell>
                          <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                            {checkIn.code}
                          </code>
                        </TableCell>
                        <TableCell className="font-medium">{checkIn.member_name}</TableCell>
                        <TableCell>{checkIn.location_name}</TableCell>
                        <TableCell>
                          {format(parseISO(checkIn.checked_in_at), "h:mm a")}
                        </TableCell>
                        <TableCell>{checkIn.checked_in_by}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-12">
                        <BarChart3 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-gray-500">No check-ins for this date</p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Monthly Report */}
        <TabsContent value="monthly" className="space-y-6">
          {/* Month/Year Picker */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                  <SelectTrigger className="w-[150px]" data-testid="month-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((m) => (
                      <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                  <SelectTrigger className="w-[100px]" data-testid="year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleExport("monthly", "csv")}
                  disabled={exporting}
                  data-testid="export-monthly-csv-btn"
                >
                  {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
                  Export CSV
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleExport("monthly", "xlsx")}
                  disabled={exporting}
                  data-testid="export-monthly-xlsx-btn"
                >
                  {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />}
                  Export Excel
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-sm text-gray-500">Total Check-ins</p>
                  <p className="text-4xl font-bold text-gray-900 mt-2">
                    {monthlyReport?.total_check_ins || 0}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-sm text-gray-500">Active Days</p>
                  <p className="text-4xl font-bold text-gray-900 mt-2">
                    {monthlyReport?.daily_breakdown?.length || 0}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-sm text-gray-500">Daily Average</p>
                  <p className="text-4xl font-bold text-gray-900 mt-2">
                    {monthlyReport?.daily_breakdown?.length > 0
                      ? Math.round(monthlyReport.total_check_ins / monthlyReport.daily_breakdown.length)
                      : 0}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Daily Breakdown Chart */}
          {monthlyReport?.daily_breakdown?.length > 0 && (
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle>Daily Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlyReport.daily_breakdown}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(date) => format(parseISO(date), "d")}
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#6b7280", fontSize: 12 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#6b7280", fontSize: 12 }}
                      />
                      <Tooltip
                        labelFormatter={(date) => format(parseISO(date), "MMMM d, yyyy")}
                        contentStyle={{
                          backgroundColor: "#fff",
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px"
                        }}
                      />
                      <Bar dataKey="count" fill="#2563eb" radius={[4, 4, 0, 0]} name="Check-ins" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* By Location Table */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Check-ins by Location</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Location</TableHead>
                    <TableHead className="text-right">Check-ins</TableHead>
                    <TableHead className="text-right">Percentage</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {monthlyReport?.by_location?.length > 0 ? (
                    monthlyReport.by_location.map((loc, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{loc.location_name}</TableCell>
                        <TableCell className="text-right">{loc.count}</TableCell>
                        <TableCell className="text-right">
                          {monthlyReport.total_check_ins > 0
                            ? ((loc.count / monthlyReport.total_check_ins) * 100).toFixed(1)
                            : 0}%
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-12">
                        <BarChart3 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-gray-500">No data for this month</p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Custom Range Report */}
        <TabsContent value="custom" className="space-y-6">
          {/* Date Range Picker */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">From:</span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="justify-start text-left font-normal"
                        data-testid="custom-start-date-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(customStartDate, "MMM d, yyyy")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={customStartDate}
                        onSelect={(date) => date && setCustomStartDate(date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">To:</span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="justify-start text-left font-normal"
                        data-testid="custom-end-date-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(customEndDate, "MMM d, yyyy")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={customEndDate}
                        onSelect={(date) => date && setCustomEndDate(date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <Button
                  onClick={handleApplyCustomRange}
                  disabled={loading}
                  data-testid="apply-custom-range-btn"
                >
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Apply
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleExport("custom", "csv")}
                  disabled={exporting || !appliedStartDate}
                  data-testid="export-custom-csv-btn"
                >
                  {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
                  Export CSV
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleExport("custom", "xlsx")}
                  disabled={exporting || !appliedStartDate}
                  data-testid="export-custom-xlsx-btn"
                >
                  {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />}
                  Export Excel
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Show message if no date range applied yet */}
          {!appliedStartDate && (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-12 text-center">
                <CalendarIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Date Range</h3>
                <p className="text-gray-500">
                  Choose a start and end date, then click "Apply" to generate your report.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Summary Stats - only show after applying */}
          {appliedStartDate && (
            <>
              <div className="grid gap-4 md:grid-cols-3">
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-blue-100 rounded-full">
                        <QrCode className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Total Check-ins</p>
                        <p className="text-2xl font-bold">{customReport?.total_check_ins || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-green-100 rounded-full">
                        <MapPin className="h-6 w-6 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Locations Active</p>
                        <p className="text-2xl font-bold">{customReport?.by_location?.length || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-purple-100 rounded-full">
                        <CalendarIcon className="h-6 w-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Days in Range</p>
                        <p className="text-2xl font-bold">{customReport?.daily_breakdown?.length || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

          {/* Daily Trend Chart */}
          {customReport?.daily_breakdown?.length > 0 && (
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle>Check-ins Over Time</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={customReport.daily_breakdown}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(d) => format(parseISO(d), "MMM d")}
                        fontSize={12}
                      />
                      <YAxis fontSize={12} />
                      <Tooltip 
                        labelFormatter={(d) => format(parseISO(d), "MMMM d, yyyy")}
                        formatter={(value) => [value, "Check-ins"]}
                      />
                      <Bar dataKey="count" fill="#2563eb" radius={[4, 4, 0, 0]} name="Check-ins" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* By Location Table */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Check-ins by Location</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Location</TableHead>
                    <TableHead className="text-right">Check-ins</TableHead>
                    <TableHead className="text-right">Percentage</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customReport?.by_location?.length > 0 ? (
                    customReport.by_location.map((loc, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{loc.location_name}</TableCell>
                        <TableCell className="text-right">{loc.count}</TableCell>
                        <TableCell className="text-right">
                          {customReport.total_check_ins > 0
                            ? ((loc.count / customReport.total_check_ins) * 100).toFixed(1)
                            : 0}%
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-12">
                        <BarChart3 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-gray-500">No data for this date range</p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Check-ins List */}
          {customReport?.check_ins?.length > 0 && (
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle>All Check-ins ({customReport.check_ins.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Member</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Date & Time</TableHead>
                      <TableHead>Checked In By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customReport.check_ins.slice(0, 100).map((ci) => (
                      <TableRow key={ci.id}>
                        <TableCell>
                          <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                            {ci.code}
                          </code>
                        </TableCell>
                        <TableCell className="font-medium">{ci.member_name}</TableCell>
                        <TableCell>{ci.location_name}</TableCell>
                        <TableCell>
                          {format(parseISO(ci.checked_in_at), "MMM d, yyyy h:mm a")}
                        </TableCell>
                        <TableCell>{ci.checked_in_by}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {customReport.check_ins.length > 100 && (
                  <p className="text-center text-sm text-gray-500 py-4">
                    Showing first 100 of {customReport.check_ins.length} check-ins. Export to see all.
                  </p>
                )}
              </CardContent>
            </Card>
          )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
